import { BuffDefinition } from '../types/buff.types';

// ---------------------------------------------------------------------------
// Buff Registry
// ---------------------------------------------------------------------------

export const BUFF_REGISTRY: Record<string, BuffDefinition> = {
  stunned: {
    id: 'stunned',
    name: 'Étourdi',
    stuns: true,
    icon: 'stun-icon',
  },
  'elite-aura': {
    id: 'elite-aura',
    name: 'Élite',
    icon: 'elite-aura',
    modifiers: [{ stat: 'size', percent: 50 }],
  },
  // ---------------------------------------------------------------------------
  // Standardized status effects
  // see docs/superpowers/specs/2026-05-25-content-bible-design.md §B0
  // ---------------------------------------------------------------------------

  bleed: {
    id: 'bleed',
    name: 'Saignement',
    icon: 'bleed',
    durationMs: 5000,
    dot: { amount: 2, intervalMs: 1000, type: 'physical', element: 'neutral' },
  },

  poison: {
    id: 'poison',
    name: 'Poison',
    icon: 'poison',
    durationMs: 5000,
    dot: { amount: 2, intervalMs: 1000, type: 'magic' },
  },

  burn: {
    id: 'burn',
    name: 'Brûlure',
    icon: 'burn',
    durationMs: 4000,
    dot: { amount: 3, intervalMs: 1000, type: 'magic', element: 'joy' },
  },

  'freeze-stack': {
    id: 'freeze-stack',
    name: 'Gel',
    icon: 'freeze',
    durationMs: 5000,
    maxStacks: 4,
    modifiers: [{ stat: 'moveSpeed', percent: -10 }],
    onMaxStacks: [
      { handler: 'applyBuff', params: { buffId: 'stunned', durationMs: 1500 } },
    ],
  },

  // ---------------------------------------------------------------------------
  // Mob-specific buffs
  // see docs/superpowers/specs/2026-05-25-content-bible-design.md §4
  // ---------------------------------------------------------------------------

  'aggro-reset': {
    id: 'aggro-reset',
    name: 'Insaisissable',
  },

  // ---------------------------------------------------------------------------
  // Alternative minor trinket buffs
  // ---------------------------------------------------------------------------

  'trap-detect': {
    id: 'trap-detect',
    name: 'Sens aiguisé',
  },

  'trap-traverse': {
    id: 'trap-traverse',
    name: 'Pas-Léger (insaisissable)',
  },

  // ---------------------------------------------------------------------------
  // Player spell buffs / debuffs
  // ---------------------------------------------------------------------------

  chill: {
    id: 'chill',
    name: 'Engourdi',
    icon: 'slow',
    durationMs: 2500,
    modifiers: [{ stat: 'moveSpeed', percent: -25 }],
  },
  'heavy-slow': {
    id: 'heavy-slow',
    name: 'Alourdi',
    icon: 'slow',
    durationMs: 3000,
    modifiers: [{ stat: 'moveSpeed', percent: -50 }],
  },
  'trap-slow': {
    id: 'trap-slow',
    name: 'Engourdi',
    icon: 'slow',
    durationMs: 120000,
    modifiers: [{ stat: 'moveSpeed', percent: -20 }],
  },
  dread: {
    id: 'dread',
    name: 'Effroi',
    icon: 'curse',
    durationMs: 4000,
    damageTakenMultiplier: 1.25,
  },

  fear: {
    id: 'fear',
    name: 'Peur',
    icon: 'curse',
    durationMs: 3000,
  },
  decay: {
    id: 'decay',
    name: 'Décrépitude',
    icon: 'poison',
    durationMs: 6000,
    dot: { amount: 3, intervalMs: 1000, type: 'magic', element: 'disgust' },
  },
  'cheer-buff': {
    id: 'cheer-buff',
    name: 'Entrain',
    icon: 'buff-attack',
    durationMs: 6000,
    modifiers: [
      { stat: 'physicalAttack', percent: 20 },
      { stat: 'magicAttack', percent: 20 },
    ],
  },
  'dodge-veil': {
    id: 'dodge-veil',
    name: 'Voile',
    icon: 'buff-dodge',
    durationMs: 3000,
    dodgeChance: 0.5,
  },

  // ---------------------------------------------------------------------------
  // Socketed modifier buffs
  // Companion buffs applied via `globalEffects` or via "tracker" hooks linked
  // to the socketed spell (cf. effectHandlers `applySpellLinkedBuff`).
  // ---------------------------------------------------------------------------

  // mod-jolt
  'mod-jolt-trigger': {
    id: 'mod-jolt-trigger',
    name: 'Sursaut (déclencheur)',
    hooks: [
      {
        event: 'onAttack',
        handler: 'applyBuff',
        params: { buffId: 'mod-jolt-burst' },
      },
    ],
  },
  'mod-jolt-burst': {
    id: 'mod-jolt-burst',
    name: 'Sursaut',
    durationMs: 3000,
    modifiers: [
      { stat: 'physicalAttack', percent: 20 },
      { stat: 'magicAttack', percent: 20 },
    ],
  },

  // mod-sharp-crit — global crit chance (per-spell crit not wired, so approximated globally)
  'mod-sharp-crit-buff': {
    id: 'mod-sharp-crit-buff',
    name: 'Crit-Aigu',
    modifiers: [
      { stat: 'critChancePhysical', amount: 10 },
      { stat: 'critChanceMagic', amount: 10 },
    ],
  },

  // mod-lifesteal
  'mod-lifesteal-buff': {
    id: 'mod-lifesteal-buff',
    name: 'Vol-de-Vie',
    lifeStealPercent: 0.05,
  },

  // mod-cyclic-death
  'mod-cyclic-death-tracker': {
    id: 'mod-cyclic-death-tracker',
    name: 'Mort-Cyclique',
    internal: true,
    hooks: [
      {
        event: 'onKill',
        handler: 'reduceLinkedSpellCooldown',
        params: { amountMs: 1000 },
      },
    ],
  },

  // mod-feast
  'mod-feast-tracker': {
    id: 'mod-feast-tracker',
    name: 'Festin (déclencheur)',
    internal: true,
    hooks: [
      {
        event: 'onKill',
        handler: 'applyBuffIfLinkedSpell',
        params: { buffId: 'mod-feast-burst' },
      },
    ],
  },
  'mod-feast-burst': {
    id: 'mod-feast-burst',
    name: 'Festin',
    durationMs: 5000,
    modifiers: [
      { stat: 'physicalAttack', percent: 25 },
      { stat: 'magicAttack', percent: 25 },
    ],
  },

  // mod-rich-drop
  // Read by MonsterEntity.computeDropRicheMultiplier via a buff lookup on the killer.
  'mod-rich-drop-tracker': {
    id: 'mod-rich-drop-tracker',
    name: 'Drop-Riche',
    internal: true,
  },

  // mod-combustion
  'mod-combustion-tracker': {
    id: 'mod-combustion-tracker',
    name: 'Combustion',
    internal: true,
    hooks: [
      {
        event: 'onKill',
        handler: 'spawnAoeOnLinkedKill',
        params: { radius: 1, damagePercent: 50, vfx: 'spell-03-7' },
      },
    ],
  },

  // mod-long-bleed
  'bleed-mod': {
    id: 'bleed-mod',
    name: 'Saignement (mod)',
    icon: 'bleed',
    durationMs: 5000,
    dot: { amount: 1, intervalMs: 1000, type: 'physical', element: 'neutral' },
  },

  // mod-persistent-slow
  'persistent-slow-buff': {
    id: 'persistent-slow-buff',
    name: 'Slow-Persistant',
    icon: 'slow',
    durationMs: 4000,
    modifiers: [{ stat: 'moveSpeed', percent: -25 }],
  },

  // mod-seeker — +40% damage vs nearest enemy (relational, uses caster context)
  'mod-seeker-buff': {
    id: 'mod-seeker-buff',
    name: 'Traqueur',
    internal: true,
    damageMultiplierConditions: [
      {
        multiplier: 1.4,
        conditions: [{ check: 'isNearestEnemy' }],
      },
    ],
  },

  // mod-momentum — stackable attack damage ramp (5 stacks, 4 s window)
  // Damage scaling lives in the socketed spell via scaleDamageByCasterBuffStacks.
  'momentum-buff': {
    id: 'momentum-buff',
    name: 'Momentum',
    durationMs: 4000,
    maxStacks: 5,
  },

  'momentum-tracker': {
    id: 'momentum-tracker',
    name: 'Momentum (tracker)',
    internal: true,
    hooks: [
      {
        event: 'onAttack',
        handler: 'addBuffStack',
        params: { buffId: 'momentum-buff' },
      },
    ],
  },

  // mod-echo-strike
  'echo-strike-tracker': {
    id: 'echo-strike-tracker',
    name: 'Echo-Strike (tracker)',
    internal: true,
    hooks: [
      {
        event: 'onAttack',
        handler: 'castLinkedSpell',
        params: {},
      },
    ],
  },

  // mod-thorns
  'thorns-buff': {
    id: 'thorns-buff',
    name: 'Epines',
    internal: true,
    damageReflectPercent: 0.2,
  },

  // mod-random-elemental
  'mod-random-elemental-debuff-joy': {
    id: 'mod-random-elemental-debuff-joy',
    name: 'Marque (joy)',
    durationMs: 5000,
    dot: { amount: 1, intervalMs: 1000, type: 'magic', element: 'joy' },
  },
  'mod-random-elemental-debuff-sadness': {
    id: 'mod-random-elemental-debuff-sadness',
    name: 'Marque (sadness)',
    durationMs: 5000,
    modifiers: [{ stat: 'moveSpeed', percent: -20 }],
  },
  'mod-random-elemental-debuff-anger': {
    id: 'mod-random-elemental-debuff-anger',
    name: 'Marque (anger)',
    durationMs: 5000,
    dot: { amount: 1, intervalMs: 500, type: 'magic', element: 'anger' },
  },
  'mod-random-elemental-debuff-fear': {
    id: 'mod-random-elemental-debuff-fear',
    name: 'Marque (fear)',
    durationMs: 5000,
    modifiers: [{ stat: 'physicalDefense', amount: -3 }],
  },
  'mod-random-elemental-debuff-surprise': {
    id: 'mod-random-elemental-debuff-surprise',
    name: 'Marque (surprise)',
    durationMs: 5000,
    modifiers: [{ stat: 'magicDefense', amount: -3 }],
  },
  'mod-random-elemental-debuff-disgust': {
    id: 'mod-random-elemental-debuff-disgust',
    name: 'Marque (disgust)',
    durationMs: 5000,
    dot: { amount: 1, intervalMs: 1000, type: 'magic', element: 'disgust' },
  },

  // ---------------------------------------------------------------------------
  // Talent statuses
  // Statuses applied to entities by talent notable passives (cf.
  // talent-tree.config.ts). The passives themselves live inline on their node.
  // ---------------------------------------------------------------------------
  'bleed-bloodletter': {
    id: 'bleed-bloodletter',
    name: 'Saignement (Saigneur)',
    icon: 'bleed',
    durationMs: 3000,
    dot: { amount: 1, intervalMs: 1000, type: 'physical', element: 'neutral' },
  },
  'talent-war-cry-burst': {
    id: 'talent-war-cry-burst',
    name: 'Cri-de-Guerre (burst)',
    durationMs: 30000,
    modifiers: [
      { stat: 'physicalAttack', percent: 20 },
      { stat: 'magicAttack', percent: 20 },
    ],
  },

  // ---------------------------------------------------------------------------
  // Risk-pact: fixed malus+bonus pair, both lasting until end of floor.
  // ---------------------------------------------------------------------------
  'pact-malus': {
    id: 'pact-malus',
    name: 'Pact (malus)',
    untilFloorEnd: true,
    modifiers: [{ stat: 'physicalDefense', percent: -20 }],
    icon: 'pact-malus',
  },
  'pact-bonus': {
    id: 'pact-bonus',
    name: 'Pact (bonus)',
    untilFloorEnd: true,
    icon: 'pact-bonus',
    modifiers: [
      { stat: 'physicalAttack', percent: 25 },
      { stat: 'magicAttack', percent: 25 },
    ],
  },

  // Elixirs: untilFloorEnd buffs are removed automatically on entering a new
  // floor by buff.utils.ts. No durationMs (incompatible with untilFloorEnd by
  // convention).
  'elixir-sharp-buff': {
    id: 'elixir-sharp-buff',
    name: 'Tranchant',
    icon: 'elixir-sharp',
    untilFloorEnd: true,
    modifiers: [
      { stat: 'physicalAttack', percent: 30 },
      { stat: 'magicAttack', percent: 30 },
    ],
  },
  'elixir-solid-buff': {
    id: 'elixir-solid-buff',
    name: 'Robuste',
    icon: 'elixir-solid',
    untilFloorEnd: true,
    modifiers: [
      { stat: 'physicalDefense', percent: 30 },
      { stat: 'magicDefense', percent: 30 },
    ],
  },
  'elixir-swift-buff': {
    id: 'elixir-swift-buff',
    name: 'Agile',
    icon: 'elixir-swift',
    untilFloorEnd: true,
    modifiers: [{ stat: 'moveSpeed', percent: 30 }],
  },

  // ---------------------------------------------------------------------------
  // New major trinkets — Walkman (Le Baladeur)
  // ---------------------------------------------------------------------------
  'walkman-tempo': {
    id: 'walkman-tempo',
    icon: 'walkman-tempo',
    name: 'Tempo',
    durationMs: 1500,
    maxStacks: 4,
    modifiers: [
      { stat: 'moveSpeed', percent: 5 },
      { stat: 'spellCastTime', percent: -5 },
    ],
  },

  // ---------------------------------------------------------------------------
  // New major trinkets — Camera (L'Appareil jetable)
  // ---------------------------------------------------------------------------
  'camera-mark': {
    id: 'camera-mark',
    name: 'Capturé',
    icon: 'view-range',
    durationMs: 6000,
  },

  // ---------------------------------------------------------------------------
  // New major trinkets — Pocketknife (Le Canif)
  // ---------------------------------------------------------------------------
  'pocketknife-bleed': {
    id: 'pocketknife-bleed',
    name: 'Saignement',
    icon: 'bleed',
    durationMs: 5000,
    maxStacks: 4,
    dot: { amount: 1, intervalMs: 1000, type: 'physical', element: 'neutral' },
  },

  // ---------------------------------------------------------------------------
  // Baseball Bat status (applied to entities)
  // ---------------------------------------------------------------------------

  'baseball-bat-slow': {
    id: 'baseball-bat-slow',
    name: 'La Batte',
    icon: 'slow',
    durationMs: 1500,
    modifiers: [{ stat: 'moveSpeed', percent: -25 }],
  },
};
